import { generateId } from '@freelancer/datastore/testing/helpers';
import type {
  AdminProject,
  AdminProjectWithOwner,
} from './admin-projects.model';
import type { ProjectAttachment } from '../project-view-projects/project-view-projects.model';
import type { ProjectViewUser } from '../project-view-users/project-view-users.model';
import { generateProjectViewUserObject } from '../project-view-users/project-view-users.seed';
import type { Project } from '../projects/projects.model';
import type { GenerateProjectOptions } from '../projects/projects.seed';
import { generateProjectObject } from '../projects/projects.seed';
import type { Skill } from '../skills/skills.model';
import { phpSkill } from '../skills/skills.seed';
import { generateUserObject } from '../users/users.seed';

export type GenerateAdminProjectOptions = GenerateProjectOptions & {
  readonly owner?: ProjectViewUser;
  readonly attachments?: readonly ProjectAttachment[];
  readonly description?: string;
  readonly isBlasted?: boolean;
  readonly skills?: readonly Skill[];
};

export function generateAdminProjectObject(
  options: GenerateAdminProjectOptions & { readonly ownerId: number },
): AdminProjectWithOwner;
export function generateAdminProjectObject(
  options: GenerateAdminProjectOptions,
): AdminProject;
export function generateAdminProjectObject(
  options: GenerateAdminProjectOptions,
): AdminProject | AdminProjectWithOwner {
  if (options.owner !== undefined && options.ownerId !== undefined) {
    if (options.owner.id !== options.ownerId) {
      throw new Error(
        'options.owner.id must match options.ownerId when both are provided',
      );
    }
  }

  // If ownerId is provided, add ownerId and use the provided options.owner or generate a random owner with that ownerId
  if (options.ownerId !== undefined) {
    return {
      ...generateProjectObject({
        ...options,
        ownerId: options.ownerId,
      }),
      ...generateAdminProjectDetails({
        ...options,
        owner:
          options.owner ??
          generateProjectViewUserObject({
            user: generateUserObject({ userId: options.ownerId }),
          }),
      }),
    };
  }

  // If ownerId is not provided, do not add ownerId but use options.owner (or create random owner)
  return {
    ...generateProjectObject(options),
    ...generateAdminProjectDetails({
      ...options,
      owner:
        options.owner ??
        generateProjectViewUserObject({
          user: generateUserObject({ userId: generateId() }),
        }),
    }),
  };
}

function generateAdminProjectDetails({
  attachments = [],
  description = 'I need someone to make me a website for my business. It should be awesome and wiz bang.',
  isBlasted = false,
  skills = [phpSkill()],
}: GenerateAdminProjectOptions): Omit<AdminProject, keyof Project> {
  return {
    attachments,
    description,
    isBlasted,
    skills,
  };
}
